import type { FC } from 'react';
import { createContext, useContext, useRef, useEffect } from 'react';
import { makeEmptyField, setField, clearField } from './state';
import { isField } from './utils';
import type { FormContext, FormState, FormField } from './@types';

export const INITIAL_STATE: FormState = {
    fields: {},
    globalErrors: [],
    submitting: false,
};

export const formContext = createContext<FormContext>({
    state: INITIAL_STATE,
    dispatch: () => null,
    touched: false,
    root: true,
    name: undefined,
});

export const moduleName = 'Form';

export function useFormField(name: string, initialValue?: string): [FormField, (value: Partial<FormField>) => void, () => void] {
    const { dispatch, state } = useContext(formContext);
    const { fields } = state;

    const setter = (field: Partial<FormField>) => {
        dispatch(setField({ name, field }));
    };

    const clear = () => {
        dispatch(clearField(name));
    };

    if (name.includes('[')) {
        const arrayAccessRegex = /(\w+)\[(\d+)\]$/;
        const match = arrayAccessRegex.exec(name);
        const arrayName = match ? match[1] : '';
        const index = match ? Number(match[2]) : 0;

        const field: FormField =
            Array.isArray(fields[arrayName]) && isField((fields[arrayName] as FormField[])[index])
                ? (fields[arrayName] as FormField[])[index]
                : makeEmptyField(initialValue);
        return [field, setter, clear];
    }
 
        const field: FormField = isField(fields[name]) ? (fields[name] as FormField) : makeEmptyField(initialValue);
        return [field, setter, clear];
    
}

export const UseFormValue: FC<{ name: string }> = ({ children, name }) => {
    const [field, setter, clear] = useFormField(name);

    if (typeof children == 'function') {
        return children([field, setter, clear]);
    }

    return children;
};

export function usePrevious<T>(value: T) {
    const ref = useRef<T>();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}
